import { ChangeEvent, useState, useEffect, useCallback } from "react";
import { IconRefresh, IconWashMachine } from "@tabler/icons-react";
import { axiosInstance } from "../../../utils/axiosInstance";
import {
  Location,
  LiveMachineRow,
  MachineSize,
} from "../../../interfaces/interfaces";
import Message from "../../Alert/Message";
import { Card, EmptyState, LoadingPanel } from "../../ui";

const MACHINE_SIZE_OPTIONS: { value: MachineSize; label: string }[] = [
  { value: "small", label: "Small (approx 8kg)" },
  { value: "medium", label: "Medium (approx 14kg)" },
  { value: "large", label: "Large (approx 18kg)" },
];

function isWasher(row: LiveMachineRow): boolean {
  return String(row.store_mac_name || "").toUpperCase().startsWith("W");
}

function sortLocations(a: Location, b: Location): number {
  const pinA = a.locationPin || "";
  const pinB = b.locationPin || "";
  const numA = Number(pinA);
  const numB = Number(pinB);
  if (!isNaN(numA) && !isNaN(numB) && pinA !== "" && pinB !== "") {
    return numA - numB;
  }
  return pinA.localeCompare(pinB);
}

export default function MachinesByLocation() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [machines, setMachines] = useState<LiveMachineRow[]>([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [message, setMessage] = useState<string | boolean>(false);
  /** Local edits keyed by mac_id (`row.id`); undefined means use server `assetTagId` */
  const [assetTagDrafts, setAssetTagDrafts] = useState<Record<string, string>>(
    {}
  );
  const [savingAssetTagForId, setSavingAssetTagForId] = useState<string | null>(
    null
  );
  const [savingSizeForId, setSavingSizeForId] = useState<string | null>(null);
  const [syncingMachines, setSyncingMachines] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLocationsLoading(true);
      try {
        const response = await axiosInstance.get<Location[]>("/location");
        if (cancelled) return;
        setLocations([...response.data].sort(sortLocations));
      } catch {
        if (!cancelled) setMessage("Failed to load locations");
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchMachines = useCallback(async (locationId: string) => {
    if (!locationId) {
      setMachines([]);
      setAssetTagDrafts({});
      return;
    }
    setAssetTagDrafts({});
    setMachinesLoading(true);
    try {
      const response = await axiosInstance.get<LiveMachineRow[]>(
        `/machine/location/${locationId}`
      );
      setMachines(response.data ?? []);
    } catch {
      setMessage("Failed to load machines for this location");
      setMachines([]);
    } finally {
      setMachinesLoading(false);
    }
  }, []);

  const assetTagInputValue = (row: LiveMachineRow) =>
    Object.prototype.hasOwnProperty.call(assetTagDrafts, row.id)
      ? assetTagDrafts[row.id]
      : (row.assetTagId ?? "");

  const assetTagIsDirty = (row: LiveMachineRow) => {
    const committed = row.assetTagId ?? "";
    return assetTagInputValue(row) !== committed;
  };

  const saveAssetTag = async (row: LiveMachineRow) => {
    const macId = row.id;
    const raw = assetTagInputValue(row).trim();
    setSavingAssetTagForId(macId);
    try {
      const { data } = await axiosInstance.patch<{
        id: string;
        assetTagId: string | null;
      }>(`/machine/${encodeURIComponent(macId)}/asset-tag`, {
        assetTagId: raw === "" ? null : raw,
      });
      setMachines((prev) =>
        prev.map((m) =>
          m.id === macId ? { ...m, assetTagId: data.assetTagId } : m
        )
      );
      setAssetTagDrafts((prev) => {
        const next = { ...prev };
        delete next[macId];
        return next;
      });
      setMessage("Asset tag saved");
    } catch {
      setMessage("Failed to save asset tag");
    } finally {
      setSavingAssetTagForId(null);
    }
  };

  const saveMachineSize = async (row: LiveMachineRow, value: string) => {
    const macId = row.id;
    setSavingSizeForId(macId);
    try {
      const { data } = await axiosInstance.patch<{
        id: string;
        machineSize: MachineSize | null;
      }>(`/machine/${encodeURIComponent(macId)}/size`, {
        machineSize: value === "" ? null : value,
      });
      setMachines((prev) =>
        prev.map((m) =>
          m.id === macId ? { ...m, machineSize: data.machineSize } : m
        )
      );
      setMessage("Machine size saved");
    } catch {
      setMessage("Failed to save machine size");
    } finally {
      setSavingSizeForId(null);
    }
  };

  const handleLocationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const id = event.target.value;
    setSelectedLocationId(id);
    void fetchMachines(id);
  };

  const selectedLocation = locations.find(
    (l) => String(l.locationId) === selectedLocationId
  );

  const canSyncSelected =
    Boolean(selectedLocation?.layout_site_id) &&
    Boolean(selectedLocation?.location_api_id) &&
    !syncingMachines;

  const handleSyncMachines = async () => {
    if (!selectedLocationId || !selectedLocation) return;
    if (!selectedLocation.layout_site_id || !selectedLocation.location_api_id) {
      setMessage(
        "Set Layout ID and API ID on the location before syncing machines"
      );
      return;
    }
    setSyncingMachines(true);
    try {
      const res = await axiosInstance.post(
        `/location/${selectedLocationId}/sync-machines`,
        {
          layoutId: String(selectedLocation.layout_site_id),
          apiId: selectedLocation.location_api_id,
        }
      );
      const data = res.data?.data;
      setMessage(
        data
          ? `Synced ${data.fetched} machine(s) (${data.liveStatusAdded} added, ${data.liveStatusUpdated} updated)`
          : res.data?.message || "Machines synced"
      );
      await fetchMachines(selectedLocationId);
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Failed to sync machines from Alliance"
      );
    } finally {
      setSyncingMachines(false);
    }
  };

  return (
    <>
      <Message message={message} setMessage={setMessage} />

      <Card
        flush
        title="Machines"
        subtitle="Asset tags and kiosk washer sizes for each location."
      >
        {locationsLoading ? (
          <LoadingPanel label="Loading locations" />
        ) : locations.length === 0 ? (
          <EmptyState title="No locations found">
            Add a location first, then come back to set up its machines.
          </EmptyState>
        ) : (
          <>
            <div className="sq-toolbar">
              <label className="sq-field is-grow" style={{ maxWidth: "28rem" }}>
                <span>Location</span>
                <select
                  className="sq-input"
                  value={selectedLocationId}
                  onChange={handleLocationChange}
                >
                  <option value="">Select a location</option>
                  {locations.map((loc) => (
                    <option key={loc.locationId} value={String(loc.locationId)}>
                      {loc.name}
                      {loc.locationPin ? ` (${loc.locationPin})` : ""}
                    </option>
                  ))}
                </select>
              </label>
              {selectedLocation ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={!canSyncSelected || machinesLoading}
                  onClick={() => void handleSyncMachines()}
                >
                  <IconRefresh size={16} />
                  {syncingMachines ? "Syncing…" : "Sync machines"}
                </button>
              ) : null}
              {selectedLocation?.layout_site_id != null &&
              selectedLocation.layout_site_id !== "" ? (
                <span className="sq-count sq-toolbar-end">
                  Site id {String(selectedLocation.layout_site_id)}
                </span>
              ) : null}
            </div>

            {!selectedLocationId ? (
              <EmptyState
                icon={<IconWashMachine size={24} />}
                title="Select a location to view its machines"
              />
            ) : machinesLoading ? (
              <LoadingPanel label="Loading machines" />
            ) : machines.length > 0 ? (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked">
                  <thead>
                    <tr>
                      <th scope="col">Store name</th>
                      <th scope="col">mac_id</th>
                      <th scope="col">Site id</th>
                      <th scope="col">Size (kiosk)</th>
                      <th scope="col">Asset tag ID</th>
                      <th scope="col" aria-label="Save asset tag" />
                    </tr>
                  </thead>
                  <tbody>
                    {machines.map((row) => (
                      <tr key={row.id}>
                        <td className="cell-title sq-strong">{row.store_mac_name}</td>
                        <td data-label="mac_id" className="cell-wide">
                          <span className="sq-mono">{row.id}</span>
                        </td>
                        <td data-label="Site id" className="cell-wide d-none d-md-table-cell">
                          {row.siteid}
                        </td>
                        <td
                          data-label="Size (kiosk)"
                          className={`cell-wide${isWasher(row) ? "" : " is-empty"}`}
                        >
                          {isWasher(row) ? (
                            <select
                              className="sq-input"
                              value={row.machineSize ?? ""}
                              disabled={savingSizeForId === row.id}
                              onChange={(e) => void saveMachineSize(row, e.target.value)}
                              aria-label={`Size for ${row.store_mac_name}`}
                            >
                              <option value="">Not set</option>
                              {MACHINE_SIZE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="sq-muted">—</span>
                          )}
                        </td>
                        <td data-label="Asset tag ID" className="cell-wide">
                          <input
                            type="text"
                            className="sq-input"
                            value={assetTagInputValue(row)}
                            onChange={(e) =>
                              setAssetTagDrafts((prev) => ({
                                ...prev,
                                [row.id]: e.target.value,
                              }))
                            }
                            placeholder="NITRO_XXXX"
                            maxLength={255}
                            aria-label={`Asset tag for ${row.store_mac_name}`}
                          />
                        </td>
                        <td className="cell-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={!assetTagIsDirty(row) || savingAssetTagForId === row.id}
                            onClick={() => void saveAssetTag(row)}
                          >
                            {savingAssetTagForId === row.id ? "Saving…" : "Save"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No machines for this location">
                There are no rows in live machine status for this site. Enter Layout ID and API ID
                on the location, then use Sync machines to pull them from Alliance.
                {!canSyncSelected && selectedLocation
                  ? ` Missing ${[
                      !selectedLocation.layout_site_id ? "Layout ID" : "",
                      !selectedLocation.location_api_id ? "API ID" : "",
                    ]
                      .filter(Boolean)
                      .join(" and ")}.`
                  : ""}
              </EmptyState>
            )}
          </>
        )}
      </Card>
    </>
  );
}
